import React, { useState, useEffect, FormEvent } from "react";
import { useParams } from "react-router-dom";
import UploadImage from "../../components/token/UploadImage";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../utils/firebase";
import { notify } from "../../utils/notify";
import NavBar from "../../components/feature/NavBar";
import Footer from "../../components/feature/Footer";

interface AgencyData {
  name: string;
  description: string;
  websiteURL: string;
  logoURL: string[];
}

const AdminAgencyModify: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [agencyName, setAgencyName] = useState("");
  const [agencyDescription, setAgencyDescription] = useState("");
  const [agencyWebsiteURL, setAgencyWebsiteURL] = useState("");
  const [logoPreviewURL, setLogoPreviewURL] = useState<string[]>([]);
  const [logoFile, setLogoFile] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch the agency data from Firestore and prepopulate the form.
  useEffect(() => {
    if (!id) return;
    const fetchAgency = async () => {
      try {
        const agencyDoc = await getDoc(doc(db, "agencies", id));
        if (agencyDoc.exists()) {
          const data = agencyDoc.data() as AgencyData;
          setAgencyName(data.name);
          setAgencyDescription(data.description);
          setAgencyWebsiteURL(data.websiteURL);
          setLogoPreviewURL(data.logoURL || []);
        } else {
          notify({ isSuccess: false, message: "Agency not found." });
        }
      } catch (error) {
        console.error("Error fetching agency:", error);
        notify({ isSuccess: false, message: "Error fetching agency data." });
      } finally {
        setLoading(false);
      }
    };
    fetchAgency();
  }, [id]);

  const handleSubmitAgency = async (e: FormEvent) => {
    e.preventDefault();
    try {
      // If new logo files were added, upload them. Otherwise, use the existing preview URLs.
      let updatedLogoURLs = logoPreviewURL;
      if (logoFile.length > 0) {
        const uploadPromises = logoFile.map(async (file) => {
          const fileName = `${Date.now()}_${file.name}`;
          const storageRef = ref(storage, `logos/${fileName}`);
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
        updatedLogoURLs = await Promise.all(uploadPromises);
      }
      const agencyDocRef = doc(db, "agencies", id!);
      await updateDoc(agencyDocRef, {
        name: agencyName,
        description: agencyDescription,
        websiteURL: agencyWebsiteURL,
        logoURL: updatedLogoURLs,
      });
      notify({ isSuccess: true, message: "Agency updated successfully." });
    } catch (error) {
      notify({ isSuccess: false, message: "An error occurred while updating the agency." });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading agency data...</p>
      </div>
    );
  }

  return (
    <>
      <NavBar />
      <div className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">Admin - Modify Agency</h1>
        <form className="space-y-6" onSubmit={handleSubmitAgency}>
          {/* Logo Upload */}
          <UploadImage
            logoFile={logoFile}
            setLogoFile={setLogoFile}
            logoPreviewURL={logoPreviewURL}
            setLogoPreviewURL={setLogoPreviewURL}
            name="logo"
          />
          {/* Name */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Name:</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              placeholder="Enter agency name..."
            />
          </div>
          {/* Description */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Description:</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={agencyDescription}
              onChange={(e) => setAgencyDescription(e.target.value)}
              placeholder="Enter agency description..."
            />
          </div>
          {/* Website URL */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Website:</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={agencyWebsiteURL}
              onChange={(e) => setAgencyWebsiteURL(e.target.value)}
              placeholder="Enter agency website URL..."
            />
          </div>
          {/* Submit Button */}
          <button
            type="submit"
            className="bg-neutral-950 text-white font-medium px-6 py-2 rounded-md"
          >
            Update Agency
          </button>
        </form>
      </div>
      <Footer />
    </>
  );
};

export default AdminAgencyModify;
